import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetOrderSampleResult } from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";
import { isNoDate } from "../../../services/dateService";
import { NotificationManager } from "react-notifications";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import {
  Science,
  Assignment,
  Description,
  DateRange,
} from "@mui/icons-material";

const ViewOrderResult = () => {
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const { orderNumber, sampleNumber, methodeCode } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getResultData(orderNumber, sampleNumber, methodeCode);
  }, [orderNumber, sampleNumber, methodeCode]);

  const getResultData = async (orderNumber, sampleNumber, methodeCode) => {
    try {
      setLoading(true);
      let response = await GetOrderSampleResult(orderNumber, sampleNumber, methodeCode);
      setResultData(response.results);
    } catch (err) {
      NotificationManager.error("Error getting result data. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display: "flex", mb: 1 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, minWidth: "160px", color: "#555" }}
      >
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ ml: 1, color: "#333" }}>
        {value || "N/A"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
          Result for Sample #{sampleNumber} (Method: {methodeCode})
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#666" }}>
          Order #{orderNumber}
        </Typography>
      </Paper>

      {resultData && (
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Assignment sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Basic Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Order Number" value={resultData.orderNumber} />
                <InfoRow label="Sample Number" value={resultData.sampleNumber} />
                <InfoRow label="Test Code" value={resultData.testCode} />
                <InfoRow label="Parameter" value={resultData.parameter} />
                <InfoRow label="Test Category" value={resultData.testCategory} />
                <InfoRow label="Test Type" value={resultData.testType} />
                <InfoRow label="Method Code" value={resultData.methodCode} />
              </CardContent>
            </Card>
          </Grid>

          {/* Analytical Values */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Science sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Analytical Results
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Analytical Value" value={resultData.analyticalValue} />
                <InfoRow label="Units" value={resultData.units} />
                <InfoRow label="Detection Limit" value={resultData.detectionLimit} />
                <InfoRow label="Reporting Limit" value={resultData.reportingLimit} />
                <InfoRow label="Detection Symbol" value={resultData.detectionSymbol} />
              </CardContent>
            </Card>
          </Grid>

          {/* Dates & Analyst */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <DateRange sx={{ color: "#1976d2", mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Analysis Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Date of Analysis" value={isNoDate(resultData.dateOfAnalysis) ? "N/A" : formatDateTime(resultData.dateOfAnalysis)} />
                <InfoRow label="Expiration Date" value={formatDateTime(resultData.expirationDate)} />
                <InfoRow label="Analyst" value={resultData.analyst} />
                <InfoRow label="Testing Status" value={resultData.testingStatus} />
              </CardContent>
            </Card>
          </Grid>

          {/* Comments */}
          {resultData.comments && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Description sx={{ color: "#1976d2", mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Comments
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Paper sx={{ p: 2, backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                    <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.6 }}>
                      {resultData.comments}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      <CommonLoader loading={loading} />
    </Box>
  );
};

export default ViewOrderResult;
